import React, { useEffect, useRef, useState } from "react";
import { X, DollarSign, Loader2, AlertTriangle, Zap, ExternalLink } from "lucide-react";
import { usageAPI } from "../../services/api.js";
import { isChannelConnected, CHANNEL_LABELS } from "../../services/notificationChannels.js";

const API_BASE = process.env.REACT_APP_API_URL ?? "";
const INR_RATE = 83.5;
const blue = "var(--app-primary-mid)";
const bord = "var(--lyra-color-border)";
const tx   = "var(--lyra-color-text)";
const mu   = "var(--app-text-3)";

function authHeaders() {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

function fmtCost(usd, currency) {
  if (currency === "INR") return `₹${(usd * INR_RATE).toFixed(2)}`;
  return `$${Number(usd).toFixed(3)}`;
}
function fmtCap(usd, currency) {
  if (currency === "INR") return `₹${(usd * INR_RATE).toFixed(0)}`;
  return `$${usd}`;
}
function fmtTok(n) { return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n); }

function Toggle({ on, onToggle }) {
  return (
    <button
      onClick={onToggle}
      style={{ position: "relative", width: 34, height: 18, borderRadius: 999, border: "none", cursor: "pointer", padding: 0, outline: "none", background: on ? "var(--lyra-color-success)" : "var(--app-border-strong)", transition: "background 0.2s", flexShrink: 0 }}
    >
      <span style={{ position: "absolute", top: 2, left: on ? 17 : 2, width: 14, height: 14, borderRadius: "50%", background: "var(--lyra-color-surface)", boxShadow: "0 1px 3px rgba(0,0,0,0.25)", transition: "left 0.2s", display: "block" }} />
    </button>
  );
}

function Badge({ label, color, bg }) {
  return <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: bg, color }}>{label}</span>;
}

// ─── Model brand colours & avatar initials ────────────────────────────────────
const MODEL_CHART_COLORS = {
  gemini:              "#1A73E8",
  groq:                "#F55036",
  "gemma-4-31b":       "#34A853",
  "mistral-7b":        "var(--lyra-color-primary)",
  "deepseek-r1":       "var(--lyra-color-primary)",
  openrouter:          "#9333EA",
  kimi:                "#0EA5E9",
  openai:              "#10A37F",
  ollama:              "var(--app-text-2)",
  together:            "var(--lyra-color-warning)",
  "claude-opus-4-5":   "#E8864D",
  "claude-sonnet-4-5": "#D4612F",
  "claude-haiku-3-5":  "#B85C2F",
};
const CHART_FALLBACK = ["#6366F1","#EC4899","#14B8A6","var(--lyra-color-warning)","#8B5CF6","#06B6D4"];
const MODEL_AVATARS  = {
  gemini:              { initials: "G",  bg: "#1A73E8" },
  groq:                { initials: "Gq", bg: "#F55036" },
  "gemma-4-31b":       { initials: "Gm", bg: "#34A853" },
  "mistral-7b":        { initials: "M",  bg: "var(--lyra-color-primary)" },
  "deepseek-r1":       { initials: "DS", bg: "var(--lyra-color-primary)" },
  openrouter:          { initials: "OR", bg: "#9333EA" },
  kimi:                { initials: "Ki", bg: "#0EA5E9" },
  openai:              { initials: "AI", bg: "#10A37F" },
  ollama:              { initials: "Ol", bg: "var(--app-text-2)" },
  together:            { initials: "To", bg: "var(--lyra-color-warning)" },
  "claude-opus-4-5":   { initials: "C●", bg: "#E8864D" },
  "claude-sonnet-4-5": { initials: "C●", bg: "#D4612F" },
  "claude-haiku-3-5":  { initials: "C●", bg: "#B85C2F" },
};
function getChartColor(id, idx) {
  return MODEL_CHART_COLORS[id] || CHART_FALLBACK[idx % CHART_FALLBACK.length];
}

// ─── SVG donut helpers ────────────────────────────────────────────────────────
function polarToCart(cx, cy, r, deg) {
  const rad = (deg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function donutArcPath(cx, cy, outerR, innerR, startDeg, endDeg) {
  const largeArc = (endDeg - startDeg) > 180 ? 1 : 0;
  const o1 = polarToCart(cx, cy, outerR, startDeg);
  const o2 = polarToCart(cx, cy, outerR, endDeg);
  const i1 = polarToCart(cx, cy, innerR, endDeg);
  const i2 = polarToCart(cx, cy, innerR, startDeg);
  const f  = v => v.toFixed(3);
  return [
    `M ${f(o1.x)} ${f(o1.y)}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${f(o2.x)} ${f(o2.y)}`,
    `L ${f(i1.x)} ${f(i1.y)}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${f(i2.x)} ${f(i2.y)}`,
    "Z",
  ].join(" ");
}

// ─── Donut chart component ────────────────────────────────────────────────────
function DonutChart({ models, currency }) {
  const [hov, setHov] = useState(null);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 768 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const sorted = [...models]
    .filter(m => m.tokensUsed > 0)
    .sort((a, b) => b.tokensUsed - a.tokensUsed);
  const totalTok = sorted.reduce((s, m) => s + m.tokensUsed, 0);

  if (sorted.length === 0 || totalTok === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 130, color: mu, fontSize: 13 }}>
        No consumption data yet this month.
      </div>
    );
  }

  const size = isMobile ? 144 : 180;
  const CX = size / 2;
  const CY = size / 2;
  const OUTER = isMobile ? 62 : 78;
  const INNER = isMobile ? 40 : 50;
  const GAP    = sorted.length > 1 ? 2.5 : 0;
  const avail  = 360 - GAP * sorted.length;
  let cursor   = -90;

  const segments = sorted.map((m, i) => {
    const color = getChartColor(m.modelId, i);
    const sweep = (m.tokensUsed / totalTok) * avail;
    const start = cursor;
    const end   = cursor + sweep;
    cursor = end + GAP;
    return { ...m, color, start, end, pct: Math.round((m.tokensUsed / totalTok) * 100) };
  });

  const hovSeg = hov !== null ? segments[hov] : null;

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, alignItems: "flex-start" }}>

      {/* ── SVG donut ── */}
      <div style={{ flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible", display: "block", margin: isMobile ? "0 auto" : 0 }}>
          <defs>
            <filter id="cb-drop" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgba(0,0,0,0.22)" />
            </filter>
            <filter id="cb-glow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* Track ring */}
          <circle cx={CX} cy={CY} r={(OUTER + INNER) / 2} fill="none" stroke="var(--app-surface-3)" strokeWidth={OUTER - INNER + 2} />

          {/* Segments */}
          {sorted.length === 1 ? (
            <circle
              cx={CX} cy={CY} r={(OUTER + INNER) / 2}
              fill="none" stroke={segments[0].color}
              strokeWidth={OUTER - INNER}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHov(0)}
              onMouseLeave={() => setHov(null)}
            />
          ) : (
            segments.map((seg, i) => (
              <path
                key={seg.modelId}
                d={donutArcPath(CX, CY, OUTER, INNER, seg.start, seg.end)}
                fill={seg.color}
                opacity={hov === null || hov === i ? 1 : 0.22}
                filter={hov === i ? "url(#cb-drop)" : undefined}
                style={{
                  transformOrigin: `${CX}px ${CY}px`,
                  transform: hov === i ? "scale(1.07)" : "scale(1)",
                  transition: "all 0.17s cubic-bezier(0.34,1.4,0.64,1)",
                  cursor: "pointer",
                }}
                onMouseEnter={() => setHov(i)}
                onMouseLeave={() => setHov(null)}
              />
            ))
          )}

          {/* Centre: primary value */}
          <text
            x={CX} y={CY - (hovSeg ? 10 : 6)}
            textAnchor="middle"
            fontSize={hovSeg ? "15" : "18"}
            fontWeight="800"
            fill={hovSeg ? hovSeg.color : tx}
            style={{ fontFamily: "'DM Sans',system-ui,sans-serif", transition: "font-size 0.15s" }}
          >
            {hovSeg ? fmtTok(hovSeg.tokensUsed) : fmtTok(totalTok)}
          </text>
          {/* Centre: label */}
          <text
            x={CX} y={CY + (hovSeg ? 6 : 10)}
            textAnchor="middle" fontSize="10" fill={mu}
            style={{ fontFamily: "'DM Sans',system-ui,sans-serif" }}
          >
            {hovSeg
              ? (hovSeg.modelName.length > 13 ? hovSeg.modelName.slice(0, 12) + "…" : hovSeg.modelName)
              : "tokens total"}
          </text>
          {/* Centre: cost when hovering */}
          {hovSeg && (
            <text
              x={CX} y={CY + 22}
              textAnchor="middle" fontSize="11" fontWeight="700"
              fill={hovSeg.estimatedCostUSD > 0 ? "var(--lyra-color-warning)" : "var(--lyra-color-success)"}
              style={{ fontFamily: "'DM Sans',system-ui,sans-serif" }}
            >
              {hovSeg.estimatedCostUSD > 0 ? fmtCost(hovSeg.estimatedCostUSD, currency) : "free tier"}
            </text>
          )}
        </svg>
      </div>

      {/* ── Legend ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4, paddingTop: 4, maxHeight: isMobile ? 220 : 176, overflowY: "auto", width: "100%" }}>
        {segments.map((seg, i) => {
          const av = MODEL_AVATARS[seg.modelId] || { initials: (seg.modelName || "?").slice(0, 2).toUpperCase(), bg: seg.color };
          return (
            <div
              key={seg.modelId}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "5px 8px", borderRadius: 9, cursor: "pointer",
                background: hov === i ? "var(--lyra-color-surface-2)" : "transparent",
                border: `1.5px solid ${hov === i ? bord : "transparent"}`,
                transition: "all 0.14s",
              }}
            >
              {/* Brand avatar */}
              <div style={{
                width: 28, height: 28, borderRadius: 8, background: av.bg, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
              }}>
                <span style={{ fontSize: 8.5, fontWeight: 800, color: "var(--app-on-accent)", letterSpacing: "-0.03em" }}>{av.initials}</span>
              </div>
              {/* Model info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: tx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {seg.modelName}
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: seg.estimatedCostUSD > 0 ? "var(--lyra-color-warning)" : "var(--lyra-color-success)", flexShrink: 0, marginLeft: 5 }}>
                    {seg.estimatedCostUSD > 0 ? fmtCost(seg.estimatedCostUSD, currency) : "free"}
                  </span>
                </div>
                {/* Proportional mini-bar */}
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                  <div style={{ flex: 1, height: 3, borderRadius: 99, background: "var(--lyra-color-border)", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: seg.pct + "%", background: seg.color, borderRadius: 99, transition: "width 0.45s" }} />
                  </div>
                  <span style={{ fontSize: 9.5, color: mu, flexShrink: 0 }}>{seg.pct}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CostBudgetPopover({
  anchorRef,
  onClose,
  currency = "USD",
  data = null,
  loading = false,
  showCurrencyToggle = false,
  onToggleCurrency,
  onRefresh, // parent's fetchCostSummary — polled every ~15s while this popover is open
}) {
  const [pos,      setPos]      = useState(null);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 768 : false));

  // Budget settings — server-persisted (previously localStorage-only, so no
  // backend cron/alert could ever see them). Loaded once on open, saved back
  // on change (debounced for the sliders).
  const [cap,      setCap]      = useState(50);
  const [alertAt,  setAlertAt]  = useState(80);
  const [alertOn,  setAlertOn]  = useState(false);
  const [alertChannels, setAlertChannels] = useState([]);
  const [channelState, setChannelState] = useState({}); // channel -> 'checking'|'connected'|'disconnected'
  const [fallback, setFallback] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Per-provider "remaining credit" — live where the provider supports it,
  // self-reported (ProviderCredit ledger minus tracked spend) otherwise.
  const [balances, setBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [showAddCredit, setShowAddCredit] = useState(false);
  const [creditProvider, setCreditProvider] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [creditSaving, setCreditSaving] = useState(false);

  const popupRef = useRef(null);

  useEffect(() => {
    usageAPI.getBudgetSettings().then((d) => {
      setCap(d.budgetCapUSD ?? 50);
      setAlertAt(d.budgetAlertPct ?? 80);
      setAlertChannels(d.budgetAlertChannels || []);
      setAlertOn((d.budgetAlertChannels || []).length > 0);
      setFallback(!!d.budgetFallbackEnabled);
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));

    setBalancesLoading(true);
    usageAPI.getBalances().then((d) => setBalances(d.balances || [])).catch(() => setBalances([])).finally(() => setBalancesLoading(false));
  }, []);

  // Poll the parent's cost-summary fetch while open, so a running agent's
  // spend visibly ticks up without closing/reopening the popover.
  useEffect(() => {
    if (typeof onRefresh !== "function") return;
    const id = setInterval(onRefresh, 15000);
    return () => clearInterval(id);
  }, [onRefresh]);

  // Position + keyboard + outside-click
  useEffect(() => {
    const onResizeOnly = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResizeOnly);

    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      if (window.innerWidth <= 768) {
        setPos({ top: "auto", left: 8, right: 8, bottom: 8, width: "auto" });
      } else {
        const popW = 440;
        const left = Math.min(window.innerWidth - popW - 8, Math.max(8, rect.left + rect.width / 2 - popW / 2));
        setPos({ top: rect.bottom + 8, left, width: popW });
      }
    } else if (window.innerWidth <= 768) {
      setPos({ top: "auto", left: 8, right: 8, bottom: 8, width: "auto" });
    }
    function onKey(e) { if (e.key === "Escape") onClose(); }
    function onOutside(e) {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutside);
    return () => {
      window.removeEventListener("resize", onResizeOnly);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onOutside);
    };
  }, [anchorRef, onClose]);

  // Note: data & loading are provided by the parent (TopBar) to avoid automatic fetching.

  // Persist settings to the server (debounced — sliders fire on every drag
  // tick, this only sends the final value after the user stops moving).
  useEffect(() => {
    if (!settingsLoaded) return; // don't overwrite the server value with the initial default on mount
    const id = setTimeout(() => {
      usageAPI.updateBudgetSettings({
        budgetCapUSD: cap,
        budgetAlertPct: alertAt,
        budgetAlertChannels: alertOn ? alertChannels : [],
        budgetFallbackEnabled: fallback,
      }).catch(() => {});
    }, 500);
    return () => clearTimeout(id);
  }, [cap, alertAt, alertOn, alertChannels, fallback, settingsLoaded]);

  const toggleAlertChannel = async (channel) => {
    const isOn = alertChannels.includes(channel);
    if (isOn) {
      setAlertChannels((prev) => prev.filter((c) => c !== channel));
      return;
    }
    setChannelState((prev) => ({ ...prev, [channel]: "checking" }));
    const connected = await isChannelConnected(channel);
    setChannelState((prev) => ({ ...prev, [channel]: connected ? "connected" : "disconnected" }));
    if (connected) setAlertChannels((prev) => (prev.includes(channel) ? prev : [...prev, channel]));
  };

  const handleAddCredit = async () => {
    const amount = Number(creditAmount);
    if (!creditProvider || !Number.isFinite(amount) || amount <= 0) return;
    setCreditSaving(true);
    try {
      await usageAPI.addCredit({ provider: creditProvider, amountUSD: amount });
      const d = await usageAPI.getBalances();
      setBalances(d.balances || []);
      setShowAddCredit(false);
      setCreditProvider("");
      setCreditAmount("");
    } catch (_) {
      // non-blocking
    } finally {
      setCreditSaving(false);
    }
  };

  if (!pos) return null;

  const summary     = data?.summary || {};
  const used        = summary.totalEstimatedCostUSD ?? 0;
  const totalTokens = summary.totalTokensUsed       ?? 0;
  const modelsUsed  = summary.modelsUsed            ?? 0;
  const subsidy     = summary.platformSubsidyConsumedUSD ?? 0;
  const pct         = Math.min(100, Math.round((used / cap) * 100));
  const models      = data?.models || [];
  const trial       = data?.trial || null;

  const dayOfMonth  = new Date().getDate() || 1;
  const projected   = (used / dayOfMonth) * 30;

  return (
    <>
      <style>{`
        @keyframes cbSlideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cbSlideUp   { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cbSpin { to { transform: rotate(360deg); } }
      `}</style>

      {isMobile && (
        <div
          onClick={onClose}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 9998 }}
        />
      )}

      <div
        ref={popupRef}
        style={{
          position: "fixed",
          top: isMobile ? "auto" : pos.top,
          left: pos.left,
          right: isMobile ? pos.right : "auto",
          bottom: isMobile ? pos.bottom : "auto",
          width: isMobile ? "auto" : pos.width,
          zIndex: 9999,
          background: "var(--lyra-color-surface)", borderRadius: 14,
          boxShadow: "0 10px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)",
          animation: isMobile ? "cbSlideUp 0.18s ease" : "cbSlideDown 0.18s ease",
          maxHeight: isMobile ? "80vh" : "90vh", overflowY: "auto",
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}
      >
        {/* ── Header ── */}
        <div style={{ padding: "14px 16px 12px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${bord}` }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--app-primary-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <DollarSign size={17} color={blue} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: tx }}>Cost & Budget</div>
            <div style={{ fontSize: 12, color: mu }}>Monthly token consumption, platform subsidy & spend cap</div>
          </div>
          {showCurrencyToggle && typeof onToggleCurrency === "function" && (
            <button
              onClick={onToggleCurrency}
              style={{
                border: `1px solid ${bord}`,
                background: "var(--lyra-color-surface)",
                color: tx,
                borderRadius: 999,
                padding: "4px 9px",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                flexShrink: 0,
              }}
              title="Toggle USD / INR"
            >
              {currency}
            </button>
          )}
          {loading && <Loader2 size={14} color={mu} style={{ animation: "cbSpin 0.7s linear infinite", flexShrink: 0 }} />}
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: mu, display: "flex", borderRadius: 7, flexShrink: 0 }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ padding: isMobile ? "12px 12px 16px" : "14px 16px 20px" }}>

          {/* ── Free trial on Lyra's own key (GPT-4o mini). Hidden for Pro and anyone using their own key. ── */}
          {trial && !trial.unlimited && (
            <div style={{ background: trial.exhausted ? "var(--lyra-color-danger-bg)" : "var(--app-primary-soft)", border: `1px solid ${trial.exhausted ? "var(--app-danger-border)" : "var(--app-primary-soft-2)"}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: tx }}>
                  {trial.exhausted || trial.sharedKeyOff ? "Free trial ended" : `Free trial · ${trial.modelName}`}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: mu }}>
                  {Math.min(trial.messagesUsed, trial.messageLimit)} / {trial.messageLimit} messages
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 99, background: "var(--lyra-color-border)", overflow: "hidden", marginBottom: 8 }}>
                <div style={{ height: "100%", width: `${Math.min(100, Math.round(Math.max(trial.messagesUsed / trial.messageLimit, trial.spentUSD / trial.capUSD) * 100))}%`, background: trial.exhausted ? "var(--lyra-color-danger)" : blue, borderRadius: 99 }} />
              </div>
              <div style={{ fontSize: 11.5, color: mu, lineHeight: 1.5, marginBottom: 10 }}>
                {trial.exhausted || trial.sharedKeyOff
                  ? "Add your own API key to keep chatting with any model."
                  : `Lyra covers ${trial.modelName} for a short trial. Add your own API key any time to use any model without limits.`}
              </div>
              <button
                onClick={() => { onClose?.(); window.location.assign("/ai-console"); }}
                style={{ border: "none", background: blue, color: "#fff", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                Add an API key
              </button>
            </div>
          )}

          {/* ── Stat tiles ── */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
            <div style={{ background: "var(--lyra-color-surface-2)", border: `1px solid ${bord}`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: tx }}>{loading ? "…" : fmtCost(used, currency)}</div>
              <div style={{ fontSize: 11, color: mu, marginTop: 2 }}>Est. cost this month</div>
              <div style={{ fontSize: 11, color: blue, marginTop: 1 }}>of {fmtCap(cap, currency)} cap</div>
            </div>
            <div style={{ background: "var(--lyra-color-surface-2)", border: `1px solid ${bord}`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: tx }}>{loading ? "…" : fmtTok(totalTokens)}</div>
              <div style={{ fontSize: 11, color: mu, marginTop: 2 }}>Tokens consumed</div>
              <div style={{ fontSize: 11, color: blue, marginTop: 1 }}>across {modelsUsed} model{modelsUsed !== 1 ? "s" : ""}</div>
            </div>
            <div style={{ background: "var(--lyra-color-surface-2)", border: `1px solid ${bord}`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "var(--lyra-color-warning)" }}>{loading ? "…" : fmtCost(projected, currency)}</div>
              <div style={{ fontSize: 11, color: mu, marginTop: 2 }}>Projected month end</div>
              <div style={{ fontSize: 11, color: mu, marginTop: 1 }}>linear estimate</div>
            </div>
          </div>

          {/* ── Budget progress bar ── */}
          <div style={{ background: "var(--lyra-color-surface-2)", border: `1px solid ${bord}`, borderRadius: 12, padding: "14px 16px", marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: tx }}>Budget used</span>
              <Badge
                label={pct + "%"}
                color={pct > 75 ? "var(--lyra-color-warning)" : "#15803d"}
                bg={pct > 75 ? "var(--lyra-color-warning-bg)" : "var(--lyra-color-success-bg)"}
              />
            </div>
            <div style={{ height: 8, borderRadius: 99, background: "var(--lyra-color-border)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: pct + "%", background: pct > 75 ? "var(--lyra-color-warning)" : "var(--lyra-color-success)", borderRadius: 99, transition: "width 0.6s" }} />
            </div>
            <div style={{ fontSize: 11.5, color: mu, marginTop: 6 }}>
              {fmtCost(used, currency)} estimated of {fmtCap(cap, currency)} cap
              {subsidy > 0 && <span> · platform subsidy: {fmtCost(subsidy, currency)}</span>}
            </div>
            {subsidy > 0 && (
              <div style={{ marginTop: 8, padding: "7px 10px", background: "var(--app-primary-soft)", borderRadius: 8, fontSize: 11.5, color: "var(--app-primary-deep)" }}>
                💙 Platform subsidised <strong>{fmtCost(subsidy, currency)}</strong> of AI credits for you this month.
              </div>
            )}
          </div>

          {/* ── Model consumption: donut chart ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: mu, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>
              Model consumption this month
            </div>
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 0", color: mu, fontSize: 13 }}>
                <Loader2 size={15} style={{ animation: "cbSpin 0.7s linear infinite", marginRight: 8 }} /> Loading…
              </div>
            ) : (
              <DonutChart models={models} currency={currency} />
            )}
          </div>

          {/* ── Per-provider remaining credit ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: mu, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Available credit
              </div>
              <button
                onClick={() => setShowAddCredit((v) => !v)}
                style={{ border: "none", background: "none", color: blue, fontSize: 11.5, fontWeight: 700, cursor: "pointer", padding: 0 }}
              >
                + Record top-up
              </button>
            </div>

            {balancesLoading ? (
              <div style={{ fontSize: 12.5, color: mu, padding: "8px 0" }}>Loading…</div>
            ) : balances.length === 0 ? (
              <div style={{ fontSize: 12.5, color: mu, padding: "8px 0" }}>
                No provider credit tracked yet — record a top-up, or connect a provider whose key can report its own balance (OpenRouter, DeepSeek, Kimi).
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                  {balances.map((b) => (
                    <div key={b.provider} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 2px" }}>
                      <span style={{
                        fontSize: 9.5, fontWeight: 700, padding: "2px 6px", borderRadius: 5, flexShrink: 0,
                        background: b.kind === "live" ? "var(--lyra-color-success-bg)" : "var(--lyra-color-surface-2)",
                        color: b.kind === "live" ? "#15803d" : mu,
                      }}>
                        {b.kind === "live" ? "LIVE" : "EST."}
                      </span>
                      <span style={{ fontSize: 12.5, color: tx, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.modelName}</span>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: tx }}>{fmtCost(b.remainingUSD, currency)}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: tx, borderTop: `1px solid ${bord}`, paddingTop: 8 }}>
                  Total available: {fmtCost(balances.reduce((s, b) => s + b.remainingUSD, 0), currency)}
                </div>
              </>
            )}

            {showAddCredit && (
              <div style={{ marginTop: 10, padding: 10, background: "var(--lyra-color-surface-2)", border: `1px solid ${bord}`, borderRadius: 10 }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <select
                    value={creditProvider}
                    onChange={(e) => setCreditProvider(e.target.value)}
                    style={{ flex: 1, border: `1px solid ${bord}`, borderRadius: 7, padding: "6px 8px", fontSize: 12, fontFamily: "inherit" }}
                  >
                    <option value="">Provider…</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic (Claude)</option>
                    <option value="gemini">Google Gemini</option>
                    <option value="groq">Groq</option>
                    <option value="together">Together AI</option>
                    <option value="minimax">MiniMax</option>
                    <option value="qwen">Qwen</option>
                    <option value="openrouter">OpenRouter</option>
                    <option value="deepseek">DeepSeek</option>
                    <option value="kimi">Kimi (Moonshot)</option>
                  </select>
                  <input
                    type="number" min="0" step="0.01" placeholder="$ amount"
                    value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)}
                    style={{ width: 90, border: `1px solid ${bord}`, borderRadius: 7, padding: "6px 8px", fontSize: 12, fontFamily: "inherit" }}
                  />
                </div>
                <button
                  onClick={handleAddCredit}
                  disabled={creditSaving || !creditProvider || !creditAmount}
                  style={{ border: "none", background: blue, color: "var(--app-on-accent)", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: creditSaving || !creditProvider || !creditAmount ? 0.6 : 1 }}
                >
                  {creditSaving ? "Saving…" : "Add"}
                </button>
              </div>
            )}
          </div>

          {/* ── Sliders ── */}
          <div style={{ marginBottom: 4 }}>
            {/* Monthly budget cap */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: tx }}>Monthly budget cap</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: blue }}>{fmtCap(cap, currency)}</span>
              </div>
              <input type="range" min={5} max={500} value={cap} onChange={e => setCap(+e.target.value)} style={{ width: "100%", accentColor: blue, cursor: "pointer" }} />
            </div>
            {/* Alert threshold */}
            <div style={{ marginBottom: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: tx }}>Alert threshold</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: blue }}>{alertAt}%</span>
              </div>
              <input type="range" min={50} max={95} value={alertAt} onChange={e => setAlertAt(+e.target.value)} style={{ width: "100%", accentColor: blue, cursor: "pointer" }} />
            </div>
          </div>

          {/* ── Toggles ── */}
          <div style={{ borderTop: `1px solid ${bord}`, paddingTop: 14 }}>
            {/* Alert toggle */}
            <div style={{ padding: "10px 0", borderBottom: `1px solid ${bord}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <AlertTriangle size={15} color="var(--lyra-color-warning)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: tx }}>Alert me at {alertAt}%</div>
                  <div style={{ fontSize: 12, color: mu, marginTop: 2 }}>Notify me via the channels below when budget threshold is hit</div>
                </div>
                <Toggle on={alertOn} onToggle={() => setAlertOn(v => !v)} />
              </div>

              {alertOn && (
                <div style={{ marginTop: 10, marginLeft: 29, display: "flex", flexDirection: "column", gap: 6 }}>
                  {["email", "telegram"].map((channel) => {
                    const checked = alertChannels.includes(channel);
                    const state = channelState[channel] || "idle";
                    return (
                      <div key={channel}>
                        <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: tx, cursor: "pointer" }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleAlertChannel(channel)} style={{ cursor: "pointer" }} />
                          {CHANNEL_LABELS[channel]}
                          {state === "checking" && <Loader2 size={11} color={mu} style={{ animation: "cbSpin 0.7s linear infinite" }} />}
                        </label>
                        {state === "disconnected" && (
                          <div style={{ marginTop: 4, marginLeft: 21, display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--lyra-color-danger)" }}>
                            <span>Connect {CHANNEL_LABELS[channel]} to receive these alerts.</span>
                            <a href={`/integrations?connect=${channel}`} target="_blank" rel="noopener noreferrer" style={{ color: "var(--lyra-color-danger)", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 2 }}>
                              Connect <ExternalLink size={10} />
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {alertChannels.length === 0 && (
                    <div style={{ fontSize: 11, color: mu }}>You'll still see this in the bell — pick a channel above to also get a message.</div>
                  )}
                </div>
              )}
            </div>
            {/* Fallback toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0" }}>
              <Zap size={15} color={blue} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: tx }}>Fallback to free model at 90%</div>
                <div style={{ fontSize: 12, color: mu, marginTop: 2 }}>Auto-switch auto-mode runs to a free-tier model to save costs</div>
              </div>
              <Toggle on={fallback} onToggle={() => setFallback(v => !v)} />
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
